// How messages show their time (#52): default, relative or exact. Saved to the account like the text size
// (/api/me/settings), and in localStorage so it applies before the account loads on this device. The words come from
// shared/src/timestamps.ts; this file holds the choice and the clock that keeps relative times current.
import { useCallback, useSyncExternalStore } from "react";
import { TIMESTAMP_FORMATS, type TimestampFormat } from "@bloom/shared";
import { api } from "./api.ts";

const KEY = "bloom:timestamp-format";
const listeners = new Set<() => void>();

function stored(): TimestampFormat {
  try {
    const v = localStorage.getItem(KEY) as TimestampFormat | null;
    return v && TIMESTAMP_FORMATS.includes(v) ? v : "default";
  } catch {
    return "default";
  }
}

let format: TimestampFormat = stored();

/** Sets it on this device right away; `save` also stores it on the account for my other devices. */
export function setTimestampFormat(next: TimestampFormat, { save = true } = {}) {
  if (!TIMESTAMP_FORMATS.includes(next)) return;
  format = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // private mode: still applies for this page
  }
  for (const fn of listeners) fn();
  if (save) void api("/me/settings", { method: "PUT", body: { timestampFormat: next } }).catch(() => {});
}

export const getTimestampFormat = () => format;

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const useTimestampFormat = (): TimestampFormat => useSyncExternalStore(subscribe, () => format);

// ---- The clock ----
// One timer per step, running only while something on screen needs it. Every time that ticks on the same step
// changes together, so a screen of relative times costs one timer, not one per message.

interface Ticker {
  now: number;
  timer: ReturnType<typeof setInterval> | null;
  listeners: Set<() => void>;
}

const tickers = new Map<number, Ticker>();

function ticker(step: number): Ticker {
  let t = tickers.get(step);
  if (!t) {
    t = { now: Date.now(), timer: null, listeners: new Set() };
    tickers.set(step, t);
  }
  return t;
}

function subscribeTick(step: number, fn: () => void) {
  const t = ticker(step);
  t.listeners.add(fn);
  if (!t.timer) {
    t.now = Date.now();
    t.timer = setInterval(() => {
      t.now = Date.now();
      for (const l of t.listeners) l();
    }, step);
  }
  return () => {
    t.listeners.delete(fn);
    if (!t.listeners.size && t.timer) {
      clearInterval(t.timer);
      t.timer = null;
    }
  };
}

const noTick = () => () => {};

/**
 * The time now, updated every `step` ms while the caller is on screen; with no step, the time of this render. Relative
 * times under two minutes count seconds (`step` 1000); everything else changes at most once a minute.
 */
export function useNow(step: number | null): number {
  const sub = useCallback((fn: () => void) => (step ? subscribeTick(step, fn) : noTick()), [step]);
  return useSyncExternalStore(sub, () => (step ? ticker(step).now : 0)) || Date.now();
}
